import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import SavedParametricDrafts from "./SavedParametricDrafts";
import type { EngineEstimateTarget } from "./SaveParametricDraft";
import TenderLpuImporter from "./TenderLpuImporter";
import { summarizeEstimatePricing } from "../domain/estimatePricingSummary";
import type {
  AppProfile,
  Estimate,
  EstimateItemPricing,
  HistoricalPriceLine,
} from "../types";

function euro(value: number | null | undefined) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function decimal(value: number | null | undefined, digits = 2) {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("pt-PT", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

type Props = {
  profile: AppProfile;
  preferredEstimateId?: string;
  onOpenLibrary: (target: EngineEstimateTarget) => void;
  onOpenEngine: (target: EngineEstimateTarget) => void;
};

type ItemDraft = {
  original_code: string;
  original_description: string;
  original_unit: string;
  quantity: string;
};

const emptyItem: ItemDraft = {
  original_code: "",
  original_description: "",
  original_unit: "m2",
  quantity: "",
};

export default function EstimateWorkbench({ profile, preferredEstimateId, onOpenEngine, onOpenLibrary }: Props) {
  const canEdit = profile.role === "admin" || profile.role === "estimator";
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [items, setItems] = useState<EstimateItemPricing[]>([]);
  const [busy, setBusy] = useState(true);
  const [itemBusy, setItemBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newReference, setNewReference] = useState("");
  const [newName, setNewName] = useState("");
  const [newClient, setNewClient] = useState("");
  const [newCoefficient, setNewCoefficient] = useState("1.30");

  const [newItem, setNewItem] = useState<ItemDraft>(emptyItem);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, HistoricalPriceLine[]>>({});
  const [historyQueries, setHistoryQueries] = useState<Record<string, string>>({});
  const [historyBusy, setHistoryBusy] = useState<string | null>(null);

  const selected = useMemo(
    () => estimates.find((estimate) => estimate.id === selectedId) ?? null,
    [estimates, selectedId],
  );

  async function loadEstimates(preferredId?: string) {
    setBusy(true);
    setError("");

    const { data, error: queryError } = await supabase
      .from("estimates")
      .select(
        "id,reference,name,client_name,estimate_type,status,default_coefficient,currency,created_at,updated_at",
      )
      .neq("status", "archived")
      .order("updated_at", { ascending: false });

    if (queryError) {
      setError(queryError.message);
      setEstimates([]);
    } else {
      const next = (data ?? []) as Estimate[];
      setEstimates(next);
      const target =
        preferredId ||
        (selectedId && next.some((row) => row.id === selectedId) ? selectedId : "") ||
        next[0]?.id ||
        "";
      setSelectedId(target);
    }

    setBusy(false);
  }

  async function loadItems(estimateId: string) {
    if (!estimateId) {
      setItems([]);
      return;
    }

    setItemBusy(true);
    setError("");

    const { data, error: queryError } = await supabase
      .from("estimate_item_pricing")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("position", { ascending: true });

    if (queryError) {
      setError(queryError.message);
      setItems([]);
    } else {
      setItems((data ?? []) as EstimateItemPricing[]);
    }

    setItemBusy(false);
  }

  useEffect(() => {
    void loadEstimates(preferredEstimateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadItems(selectedId);
  }, [selectedId]);

  async function createEstimate(event: FormEvent) {
    event.preventDefault();
    if (!canEdit || !newName.trim()) return;

    setError("");
    setNotice("");

    const coefficient = Number(newCoefficient.replace(",", "."));
    if (!Number.isFinite(coefficient) || coefficient <= 0) {
      setError("O coeficiente geral tem de ser superior a zero.");
      return;
    }

    const { data, error: insertError } = await supabase
      .from("estimates")
      .insert({
        reference: newReference.trim() || null,
        name: newName.trim(),
        client_name: newClient.trim() || null,
        estimate_type: "tender",
        default_coefficient: coefficient,
        status: "draft",
      })
      .select("id")
      .single();

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setNewReference("");
    setNewName("");
    setNewClient("");
    setNewCoefficient("1.30");
    setShowCreate(false);
    setNotice("Orçamento criado. Pode começar a introduzir linhas para preço.");
    await loadEstimates(data.id);
  }

  async function addItem(event: FormEvent) {
    event.preventDefault();
    if (!canEdit || !selected || !newItem.original_description.trim()) return;

    setError("");
    setNotice("");

    const quantity =
      newItem.quantity.trim() === ""
        ? null
        : Number(newItem.quantity.replace(",", "."));

    if (quantity !== null && (!Number.isFinite(quantity) || quantity < 0)) {
      setError("A quantidade introduzida não é válida.");
      return;
    }

    const nextPosition =
      items.reduce((max, item) => Math.max(max, Number(item.position) || 0), 0) + 10;

    const { error: insertError } = await supabase.from("estimate_items").insert({
      estimate_id: selected.id,
      position: nextPosition,
      original_code: newItem.original_code.trim() || null,
      original_description: newItem.original_description,
      original_unit: newItem.original_unit.trim() || null,
      quantity,
    });

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setNewItem(emptyItem);
    await loadItems(selected.id);
  }

  async function updateItemPricing(
    item: EstimateItemPricing,
    field: "technical_adjustment_pct" | "coefficient_override",
    rawValue: string,
  ) {
    if (!canEdit) return;

    const clean = rawValue.trim();
    const value = clean === "" && field === "coefficient_override"
      ? null
      : Number(clean.replace(",", "."));

    if (value !== null && !Number.isFinite(value)) {
      setError("Valor de ajuste/coeficiente inválido.");
      return;
    }

    const patch: Record<string, number | string | null> = {
      [field]: value,
    };

    if (field === "technical_adjustment_pct" && item.reference_dry_unit_price !== null) {
      patch.pricing_status = Number(value) === 0 ? "reference_selected" : "adjusted";
    }

    const { error: updateError } = await supabase
      .from("estimate_items")
      .update(patch)
      .eq("id", item.estimate_item_id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    await loadItems(item.estimate_id);
  }

  async function runHistorySearch(
    item: EstimateItemPricing,
    explicitQuery?: string,
  ) {
    const query =
      (explicitQuery ?? historyQueries[item.estimate_item_id] ?? item.original_description).trim();

    if (!query) return;

    setHistoryBusy(item.estimate_item_id);
    setError("");

    const { data, error: searchError } = await supabase.rpc(
      "search_source_price_lines",
      {
        search_query: query,
        result_limit: 8,
      },
    );

    if (searchError) {
      setError(searchError.message);
    } else {
      setHistory((current) => ({
        ...current,
        [item.estimate_item_id]: (data ?? []) as HistoricalPriceLine[],
      }));
    }

    setHistoryBusy(null);
  }

  async function toggleHistory(item: EstimateItemPricing) {
    if (historyFor === item.estimate_item_id) {
      setHistoryFor(null);
      return;
    }

    setHistoryFor(item.estimate_item_id);
    const initialQuery =
      historyQueries[item.estimate_item_id] ?? item.original_description;
    setHistoryQueries((current) => ({
      ...current,
      [item.estimate_item_id]: initialQuery,
    }));

    if (!history[item.estimate_item_id]) {
      await runHistorySearch(item, initialQuery);
    }
  }

  async function useHistoricalReference(
    item: EstimateItemPricing,
    source: HistoricalPriceLine,
  ) {
    if (!canEdit) return;
    setError("");
    setNotice("");

    const { error: selectError } = await supabase.rpc(
      "select_source_price_for_estimate_item",
      {
        item_id: item.estimate_item_id,
        source_line_id: source.import_line_id,
      },
    );

    if (selectError) {
      setError(selectError.message);
      return;
    }

    setNotice(
      `PU seco ${euro(source.dry_unit_price)} aplicado como referência histórica. O texto original da linha não foi alterado.`,
    );
    setHistoryFor(null);
    await loadItems(item.estimate_id);
  }

  async function updateDefaultCoefficient(rawValue: string) {
    if (!canEdit || !selected) return;

    const value = Number(rawValue.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      setError("Coeficiente geral inválido.");
      return;
    }

    const { error: updateError } = await supabase
      .from("estimates")
      .update({ default_coefficient: value })
      .eq("id", selected.id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    await loadEstimates(selected.id);
    await loadItems(selected.id);
  }

  const { dryTotal, saleTotal, pricedItems, dryIncomplete, saleIncomplete } =
    summarizeEstimatePricing(items);

  return (
    <section className="stack-xl">
      <header className="page-header">
        <div>
          <p className="eyebrow">Orçamentação</p>
          <h2>Propostas & concursos</h2>
          <p className="muted">
            A descrição original fica separada da referência histórica, do ajuste
            técnico e do coeficiente comercial.
          </p>
        </div>
        {canEdit ? (
          <div className="page-header-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                setShowImport((value) => !value);
                setShowCreate(false);
              }}
            >
              {showImport ? "Fechar importação" : "Importar LPU Excel"}
            </button>
            <button
              className="button button-primary"
              type="button"
              onClick={() => {
                setShowCreate((value) => !value);
                setShowImport(false);
              }}
            >
              {showCreate ? "Cancelar" : "Novo orçamento manual"}
            </button>
          </div>
        ) : null}
      </header>

      {showImport ? (
        <TenderLpuImporter
          profile={profile}
          onCancel={() => setShowImport(false)}
          onImported={async (estimateId) => {
            setShowImport(false);
            setNotice("LPU importada. O orçamento foi criado com as linhas originais preservadas.");
            await loadEstimates(estimateId);
            await loadItems(estimateId);
          }}
        />
      ) : null}

      {showCreate ? (
        <form className="estimate-create card" onSubmit={createEstimate}>
          <label>
            <span>Referência L37</span>
            <input
              placeholder="Ex.: O_060.26"
              value={newReference}
              onChange={(event) => setNewReference(event.target.value)}
            />
          </label>
          <label className="grow">
            <span>Nome da proposta / obra</span>
            <input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Cliente</span>
            <input value={newClient} onChange={(event) => setNewClient(event.target.value)} />
          </label>
          <label className="coef-field">
            <span>Coef. geral</span>
            <input
              inputMode="decimal"
              value={newCoefficient}
              onChange={(event) => setNewCoefficient(event.target.value)}
            />
          </label>
          <button className="button button-primary" type="submit">
            Criar
          </button>
        </form>
      ) : null}

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <div className="estimate-layout">
        <aside className="estimate-list card">
          <div className="estimate-list-title">
            <strong>Orçamentos</strong>
            <span>{estimates.length}</span>
          </div>
          {busy ? <div className="estimate-list-empty">A carregar…</div> : null}
          {!busy && estimates.length === 0 ? (
            <div className="estimate-list-empty">Ainda não existem orçamentos.</div>
          ) : null}
          {estimates.map((estimate) => (
            <button
              type="button"
              key={estimate.id}
              className={
                selectedId === estimate.id
                  ? "estimate-list-item active"
                  : "estimate-list-item"
              }
              onClick={() => setSelectedId(estimate.id)}
            >
              <span className="mono">{estimate.reference || "Sem ref."}</span>
              <strong>{estimate.name}</strong>
              <small>{estimate.client_name || "—"}</small>
            </button>
          ))}
        </aside>

        <div className="estimate-work">
          {!selected ? (
            <div className="empty card">
              <strong>Selecione ou crie um orçamento.</strong>
              <span>Aqui ficará o trabalho de preço artigo a artigo.</span>
            </div>
          ) : (
            <>
              <div className="estimate-header card">
                <div>
                  <div className="result-meta">
                    <span className="pill">{selected.status}</span>
                    <span className="mono">{selected.reference || "Sem referência"}</span>
                  </div>
                  <h3>{selected.name}</h3>
                  <span className="muted">{selected.client_name || "Sem cliente indicado"}</span>
                </div>
                <label className="coef-field">
                  <span>Coeficiente geral</span>
                  <input
                    key={String(selected.default_coefficient)}
                    inputMode="decimal"
                    defaultValue={decimal(selected.default_coefficient)}
                    disabled={!canEdit}
                    onBlur={(event) => void updateDefaultCoefficient(event.target.value)}
                  />
                </label>
              </div>

              <div className="estimate-stats">
                <div className="stat card">
                  <span>Artigos com preço</span>
                  <strong>{pricedItems}/{items.length}</strong>
                </div>
                <div className="stat card">
                  <span>{dryIncomplete ? "Subtotal seco · incompleto" : "Total seco"}</span>
                  <strong>{euro(dryTotal)}</strong>
                </div>
                <div className="stat card">
                  <span>{saleIncomplete ? "Subtotal venda · incompleto" : "Total venda"}</span>
                  <strong>{euro(saleTotal)}</strong>
                </div>
              </div>

              {canEdit ? (
                <form className="estimate-add-item card" onSubmit={addItem}>
                  <label className="code-field">
                    <span>Art.</span>
                    <input
                      value={newItem.original_code}
                      onChange={(event) =>
                        setNewItem((current) => ({
                          ...current,
                          original_code: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="grow">
                    <span>Descrição original</span>
                    <textarea
                      value={newItem.original_description}
                      onChange={(event) =>
                        setNewItem((current) => ({
                          ...current,
                          original_description: event.target.value,
                        }))
                      }
                      required
                    />
                  </label>
                  <label className="unit-small">
                    <span>Un.</span>
                    <input
                      value={newItem.original_unit}
                      onChange={(event) =>
                        setNewItem((current) => ({
                          ...current,
                          original_unit: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="qty-field">
                    <span>Qtd.</span>
                    <input
                      inputMode="decimal"
                      value={newItem.quantity}
                      onChange={(event) =>
                        setNewItem((current) => ({
                          ...current,
                          quantity: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <button className="button button-primary" type="submit">
                    Adicionar linha
                  </button>
                </form>
              ) : null}

              {itemBusy ? <div className="empty card">A carregar linhas…</div> : null}

              <div className="estimate-items">
                {items.map((item) => {
                  const open = historyFor === item.estimate_item_id;
                  const sourceRows = history[item.estimate_item_id] ?? [];
                  const itemTotals = summarizeEstimatePricing([item]);

                  return (
                    <article className="estimate-item card" key={item.estimate_item_id}>
                      <div className="estimate-item-main">
                        <div className="estimate-item-copy">
                          <div className="result-meta">
                            {item.original_code ? (
                              <span className="mono">{item.original_code}</span>
                            ) : null}
                            <span className="pill">{item.original_unit || "—"}</span>
                            <span>Qtd. {decimal(item.quantity, 2)}</span>
                            <span>{item.pricing_status}</span>
                          </div>
                          <h3>{item.original_description}</h3>
                          <button className="button button-primary" type="button"
                            onClick={() => onOpenLibrary({ itemId: item.estimate_item_id, estimateId: item.estimate_id,
                              estimateName: selected.name, code: item.original_code,
                              description: item.original_description, unit: item.original_unit })}>
                            Consultar biblioteca
                          </button>
                          {canEdit ? <button className="button button-secondary" type="button"
                            onClick={() => onOpenEngine({ itemId: item.estimate_item_id, estimateId: item.estimate_id,
                              estimateName: selected.name, code: item.original_code,
                              description: item.original_description, unit: item.original_unit })}>
                            Calcular no Gerador
                          </button> : null}
                          <button
                            type="button"
                            className="text-action"
                            onClick={() => void toggleHistory(item)}
                          >
                            {open ? "Fechar referências" : "Procurar no histórico"}
                          </button>
                        </div>

                        <div className="estimate-item-price">
                          <div>
                            <span>Referência seca</span>
                            <b>{euro(item.reference_dry_unit_price)}</b>
                          </div>
                          <label>
                            <span>Ajuste técnico %</span>
                            <input
                              key={`adj-${item.estimate_item_id}-${item.technical_adjustment_pct}`}
                              inputMode="decimal"
                              defaultValue={decimal(item.technical_adjustment_pct)}
                              disabled={!canEdit}
                              onBlur={(event) =>
                                void updateItemPricing(
                                  item,
                                  "technical_adjustment_pct",
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                          <div>
                            <span>PU seco obra</span>
                            <b>{euro(item.effective_dry_unit_price)}</b>
                          </div>
                          <label>
                            <span>Coef. artigo</span>
                            <input
                              key={`coef-${item.estimate_item_id}-${item.coefficient_override}`}
                              inputMode="decimal"
                              placeholder={decimal(item.effective_coefficient)}
                              defaultValue={
                                item.coefficient_override === null
                                  ? ""
                                  : decimal(item.coefficient_override)
                              }
                              disabled={!canEdit}
                              onBlur={(event) =>
                                void updateItemPricing(
                                  item,
                                  "coefficient_override",
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                          <div className="sale-highlight">
                            <span>PU venda</span>
                            <b>{euro(item.sale_unit_price)}</b>
                          </div>
                          <div>
                            <span>Total venda</span>
                            <b>{euro(itemTotals.saleTotal)}</b>
                          </div>
                        </div>
                      </div>

                      <SavedParametricDrafts itemId={item.estimate_item_id} />

                      {open ? (
                        <div className="estimate-history">
                          <div className="estimate-history-title">
                            <div>
                              <strong>Referências históricas</strong>
                              <span className="muted">
                                Selecione uma linha apenas como PU seco de partida.
                              </span>
                            </div>
                            <div className="estimate-history-search">
                              <input
                                type="search"
                                aria-label="Termos para pesquisar no histórico"
                                value={
                                  historyQueries[item.estimate_item_id] ??
                                  item.original_description
                                }
                                onChange={(event) =>
                                  setHistoryQueries((current) => ({
                                    ...current,
                                    [item.estimate_item_id]: event.target.value,
                                  }))
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    void runHistorySearch(item);
                                  }
                                }}
                              />
                              <button
                                className="button button-secondary"
                                type="button"
                                onClick={() => void runHistorySearch(item)}
                              >
                                Pesquisar
                              </button>
                            </div>
                          </div>

                          {historyBusy === item.estimate_item_id ? (
                            <div className="muted">A pesquisar…</div>
                          ) : null}

                          {!historyBusy && sourceRows.length === 0 ? (
                            <div className="muted">Sem resultados para esta descrição.</div>
                          ) : null}

                          {sourceRows.map((source) => (
                            <div className="estimate-history-row" key={source.import_line_id}>
                              <div>
                                <span className="muted">
                                  {source.project_name || source.source_title}
                                </span>
                                <p>{source.original_description}</p>
                              </div>
                              <span className="pill">{source.original_unit || "—"}</span>
                              <strong>{euro(source.dry_unit_price)}</strong>
                              {canEdit ? (
                                <button
                                  className="button button-secondary"
                                  type="button"
                                  onClick={() => void useHistoricalReference(item, source)}
                                >
                                  Usar PU seco
                                </button>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
